import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { calendarAPI } from '../../services/api';
import ErrorState from '../ui/ErrorState';
import { dayKey } from '../../utils/woSchedule';

/**
 * ScheduleStrip — This Week's Schedule (Mon–Fri).
 *
 * REQ-DSH-05 (operator, iter18):
 *   - Preserve the original horizontal-row layout (day label on the left,
 *     event pills to the right; today highlighted in red).
 *   - Data source: any event on the calendar this week — appointments AND
 *     scheduled work orders. Fetches via calendarAPI.getEvents.
 *   - Per-day: show the earliest event's title / truncated description /
 *     time / crew (crew supersedes assignee). If >1 event on that day,
 *     show `+N` badge for the additional items.
 *   - Clicking the row navigates to `/calendar?date=YYYY-MM-DD&view=day`.
 *   - The prior "N unscheduled" header badge is intentionally removed.
 */
export default function ScheduleStrip() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  // F-01 (2026-08-18): distinguish ERROR from EMPTY. Nullish = fetch never
  // failed (loading or genuinely empty). Non-null = fetch rejected; render
  // an alert above the strip. Same pattern as PR-42's CalendarPage.
  const [loadError, setLoadError] = useState(null);

  const weekDays = useMemo(getWeekDays, []);
  const todayStr = dayKey(new Date()); // IDI-615: local day

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const first = weekDays[0].dateStr;
        const last = weekDays[weekDays.length - 1].dateStr;
        const res = await calendarAPI.getEvents({ start: first, end: last });
        if (!cancelled) {
          setEvents(Array.isArray(res?.events) ? res.events : Array.isArray(res) ? res : []);
          setLoadError(null);
        }
      } catch (err) {
        // F-01 (2026-08-18): getEvents now propagates rejection per PR-42.
        // Without this catch the promise rejects unhandled AND the strip
        // silently renders "no schedule this week" — identical to an empty
        // successful response. Same swallow-census issue PR-42 fixed on the
        // Calendar page. Now the user sees a distinct error state.
        console.error('Failed to load week schedule:', err);
        if (!cancelled) {
          setEvents([]);
          setLoadError(err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [weekDays]);

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h3 style={titleStyle}>This Week's Schedule</h3>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} style={skeletonRowStyle} />
          ))}
        </div>
      </div>
    );
  }

  // F-01 (2026-08-18): render ERROR distinctly from EMPTY. Without this
  // branch, a rejected getEvents rendered as "no schedule this week" —
  // identical to a successful empty response. Same discipline as PR-42's
  // CalendarPage error branch. Retry hits the useEffect via the deps
  // change (setLoadError(null) on the next successful load).
  if (loadError) {
    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h3 style={titleStyle}>This Week's Schedule</h3>
        </div>
        <ErrorState
          title="Could not load the week's schedule"
          message={loadError?.message || "We couldn't reach the calendar service. Try again in a moment."}
          size="sm"
        />
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h3 style={titleStyle}>This Week's Schedule</h3>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {weekDays.map((day, idx) => {
          const isToday = day.dateStr === todayStr;
          const dayEvents = getEventsForDate(events, day.dateStr);
          const isLastDay = idx === weekDays.length - 1;
          const earliest = dayEvents[0];
          const extra = Math.max(0, dayEvents.length - 1);

          return (
            <div
              key={day.dateStr}
              onClick={() => navigate(`/calendar?date=${day.dateStr}&view=day`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/calendar?date=${day.dateStr}&view=day`);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={`View ${day.dateStr} on the calendar`}
              style={{
                ...dayRowStyle,
                ...(isToday ? todayRowStyle : {}),
                borderBottom: isLastDay ? 'none' : '1px solid var(--neutral-100)',
                cursor: 'pointer',
              }}
            >
              {/* Stacked day/date label */}
              <div style={{ ...dayLabelContainer, ...(isToday ? todayLabelStyle : {}) }}>
                <span style={dayAbbrevStyle}>{day.abbrev}</span>
                <span style={dayNumStyle}>{day.dayNum}</span>
              </div>

              {/* Events for this day: earliest event's info + optional +N badge */}
              <div style={dayEventsContainer}>
                {!earliest ? (
                  <span style={emptyDayStyle}>—</span>
                ) : (
                  <>
                    <EventChip event={earliest} />
                    {extra > 0 && (
                      <span style={extraBadgeStyle} aria-label={`${extra} more events`}>
                        +{extra}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EventChip({ event }) {
  const time = event.startTime || (event.time ? event.time.split('T')[1]?.slice(0, 5) : '');
  const crewLabel = event.crew_name || event.crewName || event.crew
    || (event.crewId || event.crew_id ? `Crew ${event.crewId || event.crew_id}` : null);
  const techLabel = Array.isArray(event.techNames) && event.techNames.length > 0
    ? event.techNames.join(', ')
    : (event.tech_name || event.techName || null);
  const assignee = crewLabel || techLabel; // crew supersedes tech
  const desc = event.description || event.customerName || event.customer_name || '';

  return (
    <span style={chipStyle} title={`${event.title || 'Untitled'}${desc ? ' — ' + desc : ''}`}>
      <span style={{ fontWeight: 600, color: 'var(--color-text-default)' }}>
        {event.title || 'Untitled'}
      </span>
      {desc && (
        <span style={chipDescStyle}>
          {desc.length > 40 ? desc.slice(0, 40) + '…' : desc}
        </span>
      )}
      {time && <span style={chipMetaStyle}>{time}</span>}
      {assignee && <span style={chipMetaStyle}>{assignee}</span>}
    </span>
  );
}

function getWeekDays() {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon...
  const monday = new Date(today);
  monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));

  const days = [];
  const abbrevs = ['MON', 'TUE', 'WED', 'THU', 'FRI'];
  for (let i = 0; i < 5; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    days.push({
      abbrev: abbrevs[i],
      dayNum: d.getDate(),
      dateStr: dayKey(d),
    });
  }
  return days;
}

// Return events for a given ISO date, sorted by start time (earliest first).
function getEventsForDate(events, dateStr) {
  if (!events || !Array.isArray(events)) return [];
  const eventDay = (e) => e.date || (e.time ? e.time.split('T')[0] : null);
  const sortKey = (e) => e.startTime || (e.time ? e.time.split('T')[1]?.slice(0, 5) : '') || '';
  // IDI-609 (D6): an appointment/task with an endDate lists on every weekday
  // it covers (up to 62 days, an end before the start is ignored).
  const covers = (e) => {
    const start = eventDay(e);
    if (!start) return false;
    if (start === dateStr) return true;
    const end = typeof e.endDate === 'string' ? e.endDate.slice(0, 10) : '';
    if (!end || end <= start || dateStr < start || dateStr > end) return false;
    const a = Date.UTC(+start.slice(0, 4), +start.slice(5, 7) - 1, +start.slice(8, 10));
    const b = Date.UTC(+dateStr.slice(0, 4), +dateStr.slice(5, 7) - 1, +dateStr.slice(8, 10));
    return (b - a) / 86400000 < 62;
  };
  return events
    .filter(covers)
    .sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
}

// ---------- Styles (matched to original ScheduleStrip layout) ----------

const containerStyle = {
  background: 'var(--color-surface)',
  border: '1px solid var(--neutral-200)',
  borderRadius: 'var(--card-radius)',
  padding: 20,
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 12,
};

const titleStyle = {
  margin: 0,
  fontSize: 16,
  fontWeight: 600,
  color: 'var(--color-text-default)',
};

const skeletonRowStyle = {
  height: 40,
  borderRadius: 6,
  background: 'linear-gradient(90deg, var(--neutral-100) 25%, var(--neutral-200) 50%, var(--neutral-100) 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.5s infinite',
};

const dayRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '8px 10px',
  borderLeft: '3px solid transparent',
  transition: 'background 0.1s',
};

const todayRowStyle = {
  borderLeft: '3px solid var(--color-action-primary)',
  background: 'var(--neutral-50)',
};

const dayLabelContainer = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minWidth: 36,
};

const todayLabelStyle = {
  color: 'var(--color-action-primary)',
};

const dayAbbrevStyle = {
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: 'inherit',
};

const dayNumStyle = {
  fontSize: 16,
  fontWeight: 700,
  color: 'inherit',
};

const dayEventsContainer = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  flex: 1,
  alignItems: 'center',
};

const emptyDayStyle = {
  fontSize: 13,
  color: 'var(--color-text-muted)',
};

const chipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  padding: '3px 8px',
  borderRadius: 4,
  background: 'var(--neutral-50)',
  border: '1px solid var(--neutral-200)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '100%',
};

const chipDescStyle = {
  color: 'var(--color-text-muted)',
  maxWidth: 200,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const chipMetaStyle = {
  color: 'var(--color-text-muted)',
  fontSize: 11,
};

const extraBadgeStyle = {
  fontSize: 11,
  fontWeight: 700,
  padding: '2px 8px',
  borderRadius: 'var(--r-pill)',
  background: 'var(--neutral-100)',
  color: 'var(--color-text-default)',
  border: '1px solid var(--neutral-200)',
};
